import { z } from 'zod'
import { FileProcessingStatusSchema, UserFileProcessingStatus, type FileProcessingStatus } from '@/user_files/schemas/processing-status'
import { ExternalFileLinkMetadataSchema } from '@/user_files/external-storage/schemas'

// ============================================================================
// Init Upload Schemas (POST /api/v1/user-files/init-upload)
// ============================================================================

/**
 * Request type for POST /api/v1/user-files/init-upload
 */
export type InitUploadRequest = {
  supports_native_gcs_signed_upload: true
  files: Array<{
    client_tmp_id: string
    filename: string
    folder_id: string
  }>
}

export enum UserFileUploadTransport {
  NativeGcsSigned = 'native_gcs_signed',
}

export const UserFileUploadTransportSchema = z.nativeEnum(UserFileUploadTransport)

/**
 * Response schema for POST /api/v1/user-files/init-upload
 */
export const InitUploadFileResponseSchema = z.object({
  client_tmp_id: z.string(),
  file_id: z.string().uuid(),
  storage_path: z.string(),
  upload_transport: z.literal(UserFileUploadTransport.NativeGcsSigned),
  upload_object_key: z.string().min(1),
})

export type InitUploadFileResponse = z.infer<typeof InitUploadFileResponseSchema>

export const InitUploadResponseSchema = z.object({
  files: z.array(InitUploadFileResponseSchema),
})

export type InitUploadResponse = z.infer<typeof InitUploadResponseSchema>

export const SignedUploadUrlResponseSchema = z.object({
  file_id: z.string().uuid(),
  upload_url: z.string().min(1),
  expires_at: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string()),
  upload_object_key: z.string().min(1),
})

export type SignedUploadUrlResponse = z.infer<typeof SignedUploadUrlResponseSchema>

// ============================================================================
// Shared schemas
// ============================================================================

export { FileProcessingStatusSchema, UserFileProcessingStatus }
export type { FileProcessingStatus }

/**
 * Known reducto job status values.
 * We keep this list for UI mapping, but use a string schema for forward compatibility.
 */
export const KNOWN_REDUCTO_JOB_STATUSES = ['QUEUED', 'SUBMITTED', 'WEBHOOK_RECEIVED', 'RETRIEVING', 'COMPLETED', 'FAILED'] as const

export type KnownReductoJobStatus = (typeof KNOWN_REDUCTO_JOB_STATUSES)[number]

export const ReductoJobStatusSchema = z.string()
export type ReductoJobStatus = z.infer<typeof ReductoJobStatusSchema>

// ============================================================================
// Backend Schema Mirrors - GET /api/v1/user-files/batch?file_ids=...
// ============================================================================

/**
 * UserFileViewableContent - mirrors backend UserFileViewableContent.
 *
 * The representation the FE loads to view a file's contents in-app - either the original
 * file or a generated display asset, so both `gcs_path` and `content_type` can differ
 * from the original upload.
 */
export const UserFileViewableContentSchema = z.object({
  gcs_path: z.string(),
  content_type: z.string(),
})

export type UserFileViewableContent = z.infer<typeof UserFileViewableContentSchema>

/** UserFileRead - mirrors backend UserFilePublicRead Pydantic schema */
export const UserFileReadSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  matter_id: z.string().uuid().nullable(),
  folder_id: z.string().uuid(),
  storage_folder: z.string(),
  original_filename: z.string(),
  detected_content_type: z.string().nullable(),
  detected_extension: z.string().nullable(),
  verified: z.boolean(),
  size_bytes: z.number().nullable(),
  processing_status: FileProcessingStatusSchema,
  processing_error: z.string().nullable(),
  deleted_at: z.string().nullable(),
  deleted_by_folder_id: z.string().uuid().nullable(),
  storage_path: z.string(),
  is_deleted: z.boolean(),
  is_directly_deleted: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  viewable_content: UserFileViewableContentSchema.nullish(),
  external_storage: ExternalFileLinkMetadataSchema.nullable().optional(),
})

export type UserFileRead = z.infer<typeof UserFileReadSchema>

/**
 * ReductoJobSafeRead - mirrors backend ReductoJobSafeRead Pydantic schema
 */
export const ReductoJobSafeReadSchema = z.object({
  id: z.string().uuid(),
  file_id: z.string().uuid(),
  reducto_job_id: z.string().nullable(),
  status: ReductoJobStatusSchema,
  error_message: z.string().nullable(),
  num_pages: z.number().nullable(),
  total_pages: z.number().nullable(),
  duration: z.number().nullable(), // float seconds (was duration_ms in milliseconds)
  created_at: z.string(),
  updated_at: z.string(),
})

export type ReductoJobSafeRead = z.infer<typeof ReductoJobSafeReadSchema>

/**
 * UserFileAssetRead - mirrors backend UserFileAssetRead Pydantic schema
 */
export const UserFileAssetReadSchema = z.object({
  id: z.string().uuid(),
  file_id: z.string().uuid(),
  user_id: z.string(),
  reducto_job_id: z.string().uuid().nullable(),
  storage_folder: z.string(),
  filename: z.string(),
  extension: z.string(),
  content_type: z.string(),
  size_bytes: z.number().nullable(),
  gcs_path: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type UserFileAssetRead = z.infer<typeof UserFileAssetReadSchema>

/**
 * FileData - single file's complete data from unified endpoint
 */
export const FileDataSchema = z.object({
  record: UserFileReadSchema,
  reducto_job: ReductoJobSafeReadSchema.nullable(),
  assets: z.array(UserFileAssetReadSchema),
})

export type FileData = z.infer<typeof FileDataSchema>

/**
 * FileDataBatchResponse - response from GET /api/v1/user-files/batch?file_ids=...
 */
export const FileDataBatchResponseSchema = z.object({
  files: z.record(z.string(), FileDataSchema),
})

export type FileDataBatchResponse = z.infer<typeof FileDataBatchResponseSchema>
